import { TRPCClientError } from "@trpc/client";
import type { MessageKey, MessageParams } from "@ec2/i18n";

export interface LocalizedError {
  key: MessageKey;
  params?: MessageParams;
}

/** tRPC error code from any thrown value (null for non-tRPC failures). */
export function errorCode(err: unknown): string | null {
  if (err instanceof TRPCClientError) {
    const data = err.data as { code?: string } | undefined;
    return data?.code ?? null;
  }
  return null;
}

/**
 * Cooldown seconds for TOO_MANY_REQUESTS: prefer the structured
 * `error.data.retryAfterSeconds` (set by the contracts errorFormatter);
 * fall back to parsing "try again in Ns" out of the message for older
 * servers, then to 60 when neither is available.
 */
export function retryAfterSeconds(err: unknown): number {
  if (err instanceof TRPCClientError) {
    const data = err.data as { retryAfterSeconds?: unknown } | undefined;
    if (typeof data?.retryAfterSeconds === "number") return data.retryAfterSeconds;
  }
  const message = err instanceof Error ? err.message : "";
  const match = /(\d+)\s*s/.exec(message);
  return match ? Number(match[1]) : 60;
}

/**
 * Scanner failures, localized BY CODE (never the raw server message — the
 * server speaks English only). Garbage input never crashes: unknown codes get
 * the generic scan-failed line.
 */
export function scanError(err: unknown): LocalizedError {
  switch (errorCode(err)) {
    case "NOT_FOUND":
      return { key: "ec2.scanner.unknown" };
    case "PRECONDITION_FAILED":
      return { key: "ec2.scanner.notEligible" };
    case "CONFLICT":
      return { key: "ec2.scanner.alreadyDone" };
    case "TOO_MANY_REQUESTS":
      return { key: "ec2.scanner.cooldown", params: { seconds: retryAfterSeconds(err) } };
    default:
      return { key: "ec2.error.generic" };
  }
}

/**
 * event.join failures. PRECONDITION_FAILED there means one thing — the event
 * is over — and saying so beats the generic "that doesn't work right now",
 * which read like an outage.
 */
export function joinError(err: unknown): LocalizedError {
  switch (errorCode(err)) {
    case "NOT_FOUND":
      return { key: "ec2.join.notFound" };
    case "PRECONDITION_FAILED":
      return { key: "ec2.join.ended" };
    default:
      return generalError(err);
  }
}

/** Non-scanner surfaces: one friendly line per code family. */
export function generalError(err: unknown): LocalizedError {
  switch (errorCode(err)) {
    case "NOT_FOUND":
      return { key: "ec2.error.notFound" };
    case "CONFLICT":
      return { key: "ec2.error.conflict" };
    case "PRECONDITION_FAILED":
      return { key: "ec2.error.precondition" };
    case "TOO_MANY_REQUESTS":
      return { key: "ec2.error.tooMany", params: { seconds: retryAfterSeconds(err) } };
    case "UNAUTHORIZED":
      return { key: "ec2.error.session" };
    default:
      return { key: "ec2.error.generic" };
  }
}
